import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { isMilestoneScheduleEndDate } from '@sequencehq/utils'
import { isAfter, MINUTES, startOfDay } from '@sequencehq/utils/dates'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { useMemo } from 'react'
import { getAmendmentEffectiveFromBounds } from '../SelectScheduleForAmendment/getAmendmentEffectiveFromBounds'
import { AmendableSchedule } from '../SelectScheduleForAmendment/useAmendableSchedules'

/**
 * The schedule the quote amends, and the dates its effective date can move
 * between. `unavailableReason` is set whenever re-applying the schedule would
 * fail, so the control can be disabled up front rather than erroring on confirm.
 */
export const useAmendedSchedule = () => {
  const cubeContext = useCubeContext()
  const billingScheduleId =
    cubeContext.queries.rawData.data.quote.amendment?.billingScheduleId

  const scheduleQuery = useQuery(
    dashboard20240730Client.getBillingSchedule,
    { id: billingScheduleId ?? '' },
    { enabled: Boolean(billingScheduleId), staleTime: 1 * MINUTES }
  )

  const schedule = scheduleQuery.data ?? undefined

  const bounds = useMemo(
    () => (schedule ? getAmendmentEffectiveFromBounds(schedule) : undefined),
    [schedule]
  )

  const unavailableReason = scheduleQuery.isError
    ? 'The amended schedule could not be loaded'
    : schedule && getAmendedScheduleUnavailableReason(schedule)

  return {
    schedule,
    bounds,
    isLoading: scheduleQuery.isLoading,
    unavailableReason
  }
}

/**
 * Each of these would make `splitSchedulePhasesAtDate` throw, or leave no date
 * that could be picked.
 */
export const getAmendedScheduleUnavailableReason = (
  schedule: AmendableSchedule
) => {
  if (schedule.status !== 'ACTIVE') {
    return 'The amended schedule is no longer active'
  }

  const isMilestoneBased = schedule.phases.some(phase =>
    isMilestoneScheduleEndDate(phase.endDate ?? '')
  )

  if (isMilestoneBased) {
    return 'Milestone based schedules cannot be amended'
  }

  const { minDate, maxDate } = getAmendmentEffectiveFromBounds(schedule)
  // The minimum can be now rather than midnight, so it's compared by day
  const hasNoDateToPick =
    maxDate !== null && isAfter(startOfDay(minDate), maxDate)

  if (hasNoDateToPick) {
    return 'The amended schedule has no dates left to amend from'
  }

  return undefined
}
